import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './DatePicker.module.css';
import { getClassName } from '../../utils/className.js';
import { withFloatingLabel } from '../FloatingLabel/withFloatingLabel';

/**
 * Feather-inspired calendar icon SVG.
 */
const CalendarIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/**
 * Helper to convert a Date or string value into a Date object.
 */
const parseToDate = (val) => {
  if (!val) {
    return null;
  }
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val;
  }
  const date = new Date(val);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formats a Date object as a string matching format (e.g. dd/MM/yyyy).
 */
const formatCalendarDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date) {
    return '';
  }
  const d = parseToDate(date);
  if (!d) {
    return '';
  }
  const pad = (n) => String(n).padStart(2, '0');
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  return formatStr
    .replace(/dd/gi, day)
    .replace(/mm/gi, month)
    .replace(/yyyy/gi, year);
};

/**
 * Generates the 42 cells needed to display a 6-week calendar grid.
 */
const getCalendarCells = (year, month) => {
  const cells = [];

  // Previous month details
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

  // Current month details
  const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay(); // 0 = Sunday, 1 = Monday

  // 1. Fill previous month's overlapping days
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    cells.push({
      date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
      isCurrentMonth: false,
      dayNum: daysInPrevMonth - i
    });
  }

  // 2. Fill current month's days
  for (let i = 1; i <= daysInCurrentMonth; i++) {
    cells.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
      dayNum: i
    });
  }

  // 3. Fill next month's overlapping days to complete the 6-week grid (42 cells)
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  const remainingCells = 42 - cells.length;
  for (let i = 1; i <= remainingCells; i++) {
    cells.push({
      date: new Date(nextYear, nextMonth, i),
      isCurrentMonth: false,
      dayNum: i
    });
  }

  return cells;
};

const defaultMonthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const getMonthName = (monthIndex, customMonthNames) => {
  if (!customMonthNames) {
    return defaultMonthNames[monthIndex];
  }
  if (Array.isArray(customMonthNames)) {
    return customMonthNames[monthIndex] || defaultMonthNames[monthIndex];
  }
  if (typeof customMonthNames === 'object') {
    if (customMonthNames[monthIndex] !== undefined) {
      return customMonthNames[monthIndex];
    }
    const englishName = defaultMonthNames[monthIndex];
    if (customMonthNames[englishName] !== undefined) {
      return customMonthNames[englishName];
    }
  }
  return defaultMonthNames[monthIndex];
};

/**
 * A custom, self-contained DatePicker component implemented without any plugin dependencies.
 * Exposes standard props matching `@material-ui/pickers` components.
 * Renders the calendar popover inside a React Portal to prevent layout cutoff issues.
 *
 * @component
 * @param {Object} props
 * @param {Date|string|null} [props.value] - The selected date.
 * @param {(date: Date|null) => void} [props.onChange] - Callback fired when value changes. Receives Date object or null.
 * @param {string} [props.label] - Optional label shown above the input.
 * @param {boolean|string} [props.error] - If boolean, shows error border. If string, also displays error message.
 * @param {boolean} [props.disabled=false] - Disables the input.
 * @param {boolean} [props.fullWidth=false] - Stretch to the container width.
 * @param {Date|string} [props.minDate] - Minimum selectable date.
 * @param {Date|string} [props.maxDate] - Maximum selectable date.
 * @param {'small'|'medium'} [props.size='medium'] - Visual size/height: small (34px) or medium (40px).
 * @param {string} [props.placeholder='Select Date'] - Placeholder shown when empty.
 * @param {string} [props.format='dd/MM/yyyy'] - Date display format.
 * @param {boolean} [props.autoCloseOnSelect=true] - If true, the picker will automatically close when a date is selected.
 * @param {string} [props.className] - Class name for the outer wrapper.
 * @param {string} [props.inputClassName] - Class name for the native input element.
 * @param {React.CSSProperties} [props.style] - Styles for the outer wrapper.
 * @param {string[]|Object} [props.monthNames] - Custom month names array or object mapping indices/English names to translations.
 */
const DatePicker = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
  fullWidth = false,
  minDate,
  maxDate,
  size = 'medium',
  placeholder = 'Select Date',
  format: formatStr = 'dd/MM/yyyy',
  autoCloseOnSelect = true,
  className,
  inputClassName,
  style,
  monthNames,
  floatingLabel = false,
  isFloating = false,
  ...rest
}) => {
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const selectedDate = parseToDate(value);

  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const [animatingMonth, setAnimatingMonth] = useState(null);
  const [direction, setDirection] = useState('next');
  const prevMonthRef = useRef(currentMonth);

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate]);

  const handleMonthChange = (newMonth) => {
    if (animatingMonth) {
      return;
    }
    const isNext = newMonth.getTime() > prevMonthRef.current.getTime();
    setDirection(isNext ? 'next' : 'prev');
    setAnimatingMonth(prevMonthRef.current);
    prevMonthRef.current = newMonth;
    setCurrentMonth(newMonth);
  };

  useEffect(() => {
    if (animatingMonth) {
      const timer = setTimeout(() => {
        setAnimatingMonth(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [animatingMonth]);

  const updateCoords = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateCoords();
      window.addEventListener('resize', updateCoords);
      window.addEventListener('scroll', updateCoords, true);
    }
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current && containerRef.current.contains(event.target);
      const inPopover = popoverRef.current && popoverRef.current.contains(event.target);
      if (!inContainer && !inPopover) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e) => {
    e.preventDefault();
    if (disabled) {
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleDayClick = (day) => {
    onChange?.(day);
    if (autoCloseOnSelect) {
      setIsOpen(false);
    }
  };

  const handlePrevMonth = () => {
    if (animatingMonth) {
      return;
    }
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    handleMonthChange(newMonth);
  };

  const handleNextMonth = () => {
    if (animatingMonth) {
      return;
    }
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    handleMonthChange(newMonth);
  };

  const parsedMin = parseToDate(minDate);
  const parsedMax = parseToDate(maxDate);

  const wrapperClassName = [
    styles.datePicker,
    fullWidth && styles.datePickerFull,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const fieldClassName = getClassName(
    styles.datePickerInput,
    size === 'small' && styles.datePickerSmall,
    error && styles.datePickerInputError,
    selectedDate && styles.hasValue,
    floatingLabel && styles.inputWithFloatingLabel,
    inputClassName,
  )

  const renderCalendar = (monthToRender) => {
    const year = monthToRender.getFullYear();
    const month = monthToRender.getMonth();
    const cells = getCalendarCells(year, month);
    const weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    return (
      <div className={styles.monthWrapper}>
        <div className={styles.caption}>
          {getMonthName(month, monthNames)} {year}
        </div>
        <div className={styles.calendarGridContainer}>
          <div className={styles.calendarGrid}>
            {weekdays.map((wd) => (
              <div key={wd} className={styles.weekdayHeader}>
                {wd}
              </div>
            ))}
            {cells.map((cell, idx) => {
              const isSelected = selectedDate &&
                cell.date.getDate() === selectedDate.getDate() &&
                cell.date.getMonth() === selectedDate.getMonth() &&
                cell.date.getFullYear() === selectedDate.getFullYear();

              const today = new Date();
              const isToday =
                cell.date.getDate() === today.getDate() &&
                cell.date.getMonth() === today.getMonth() &&
                cell.date.getFullYear() === today.getFullYear();

              let isDisabled = false;
              if (parsedMin) {
                const minTime = new Date(parsedMin).setHours(0, 0, 0, 0);
                if (cell.date.getTime() < minTime) {
                  isDisabled = true;
                }
              }
              if (parsedMax) {
                const maxTime = new Date(parsedMax).setHours(23, 59, 59, 999);
                if (cell.date.getTime() > maxTime) {
                  isDisabled = true;
                }
              }

              const cellClass = [
                styles.dayCell,
                !cell.isCurrentMonth && styles.dayOutside,
                isToday && styles.dayToday,
                isSelected && styles.daySelected,
                isDisabled && styles.dayDisabled,
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div
                  key={idx}
                  className={cellClass}
                  onClick={() => !isDisabled && handleDayClick(cell.date)}
                >
                  {cell.dayNum}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const inputWrapperMarkup = (
    <div ref={floatingLabel ? containerRef : null} className={styles.inputWrapper}>
      <input
        className={fieldClassName}
        type="text"
        value={formatCalendarDate(selectedDate, formatStr)}
        readOnly
        disabled={disabled}
        onClick={handleToggle}
        placeholder={placeholder}
        {...rest}
      />
      <button
        type="button"
        className={styles.iconButton}
        disabled={disabled}
        onClick={handleToggle}
        tabIndex="-1"
        aria-label="Open calendar"
      >
        <CalendarIcon />
      </button>

      {isOpen && createPortal(
        <div
          ref={popoverRef}
          className={styles.calendarPopover}
          style={{
            top: `${coords.top}px`,
            left: `${coords.left}px`,
          }}
        >
          <button
            type="button"
            className={`${styles.navButton} ${styles.navButtonPrev}`}
            onClick={handlePrevMonth}
            disabled={disabled || !!animatingMonth}
            aria-label="Previous month"
          />
          <button
            type="button"
            className={`${styles.navButton} ${styles.navButtonNext}`}
            onClick={handleNextMonth}
            disabled={disabled || !!animatingMonth}
            aria-label="Next month"
          />

          <div className={styles.sliderViewport}>
            {animatingMonth ? (
              <div
                className={direction === 'next' ? styles.sliderTrackNext : styles.sliderTrackPrev}
              >
                {direction === 'next' ? (
                  <>
                    {renderCalendar(animatingMonth)}
                    {renderCalendar(currentMonth)}
                  </>
                ) : (
                  <>
                    {renderCalendar(currentMonth)}
                    {renderCalendar(animatingMonth)}
                  </>
                )}
              </div>
            ) : (
              renderCalendar(currentMonth)
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  if (floatingLabel) {
    return inputWrapperMarkup;
  }

  return (
    <div ref={floatingLabel ? null : containerRef} className={wrapperClassName} style={style}>
      {label != null && <label className={styles.datePickerLabel}>{label}</label>}
      {inputWrapperMarkup}
      {typeof error === 'string' && error && (
        <span className={styles.datePickerError}>{error}</span>
      )}
    </div>
  );
};

export default withFloatingLabel(DatePicker);
